import { Injectable } from '@angular/core'
import { BaseSignalStore } from '@core/state/base-store'
import { DemandeDocumentResponse } from '@shared/models/dto/api-dtos'

/**
 * State interface for Document Requests store
 * Manages document requests for a dossier
 */
export interface DemandeDocumentsState {
  dossierId: string | null
  requests: DemandeDocumentResponse[]
  recipientRole: number | null // Role enum value
}

/**
 * Store for managing Document Requests state
 * Handles data, loading, error states for document requests
 * Provides reactive selectors for components to subscribe to state changes
 */
@Injectable({ providedIn: 'root' })
export class DemandeDocumentsStore extends BaseSignalStore<DemandeDocumentsState> {
  constructor() {
    super({
      dossierId: null,
      requests: [],
      recipientRole: null,
    })
  }

  // Computed signal selectors (reactive, use in templates with signals)
  readonly dossierId = this.select(state => state.dossierId)
  readonly requests = this.select(state => state.requests)
  readonly recipientRole = this.select(state => state.recipientRole)
  readonly hasRequests = this.select(state => state.requests.length > 0)
  readonly requestsCount = this.select(state => state.requests.length)

  // Observable selectors for RxJS integration (use in components with async pipe)
  readonly dossierId$ = this.select$(state => state.dossierId)
  readonly requests$ = this.select$(state => state.requests)
  readonly recipientRole$ = this.select$(state => state.recipientRole)
  readonly hasRequests$ = this.select$(state => state.requests.length > 0)
  readonly requestsCount$ = this.select$(state => state.requests.length)

  /**
   * Sets all document requests for a dossier
   * @param requests Array of DemandeDocumentResponse
   * @param dossierId The dossier ID
   * @param recipientRole The recipient role filter
   */
  setRequests(requests: DemandeDocumentResponse[], dossierId: string, recipientRole: number): void {
    this.setState({
      requests,
      dossierId,
      recipientRole,
    })
  }

  /**
   * Adds a new request to the store
   * @param request New DemandeDocumentResponse
   */
  addRequest(request: DemandeDocumentResponse): void {
    this.setState({
      requests: [...this.state().requests, request],
    })
  }

  /**
   * Removes a request from the store
   * @param requestId ID of the request to remove
   */
  removeRequest(requestId: number): void {
    this.setState({
      requests: this.state().requests.filter(req => req.id !== requestId),
    })
  }

  /**
   * Resets the store to initial state
   * Clears all requests data
   */
  override reset(): void {
    super.reset()
  }
}
